import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import moment from 'moment';
import Avatar from '@components/@core/Avatar';
import Button from '@components/@core/AnimatedButton';
import ErrorBoundary from '@components/@core/ErrorBoundary';
import Rating from '@components/@core/Rating';
import Thumbnail from '@components/@core/Thumbnail';
import AppActions from '~/actions/app';
import { convertUrl, getCompanyName, hasHtml } from '~/services/util';
window.AppActions = AppActions;
const Detail = ({ business }) => {
    const navigate = useNavigate();
    const profile = business?.data?.profile || business?.hcms?.data?.profile;
    const products = business?.products || business?.vrs?.products;
    const description = profile?.about || business?.vrs?.data?.description || 'Not Provided';
    const avatar = business?.vrs?.avatar || '';

    const dispatch = useDispatch();
    window.dispatch = dispatch;
    const [allProducts, setAllProducts] = useState(false);
    const [allUniversities, setAllUniversities] = useState(false);
    const [allBlogs, setAllBlogs] = useState(false);

    const { street, city, state, zip } = profile?.address || { street: '', city: '', state: '', zip: '' };
    const addressValues = [street, city, state, zip];
    const address = addressValues?.map((x, i) => (x ? `${x}${i !== addressValues.length - 1 ? ', ' : ''} ` : '')).join('');

    const websiteUrl = useMemo(() => {
        const url = profile?.url || '';
        if (url.includes('http://') || url.includes('https://')) {
            return url;
        } else {
            return `https://${url}`;
        }
    }, [profile?.url]);

    const handleClickAll = (type) => () => {
        if (type === 'product') {
            setAllProducts(!allProducts);
        }
        if (type === 'univeristy') {
            setAllUniversities(!allUniversities);
        }
        if (type === 'blog') {
            setAllBlogs(!allBlogs);
        }
    };

    const handleOpenRating = (data) => (e) => {
        e.stopPropagation();
        const companies = business.vrs ? [business.vrs] : [];
        const payload = {
            type: 'Rate Vendor',
            data: {
                before: { company: data?.parent_id, category: data?.data?.category, product: data?.id, companies },
            },
        };
        dispatch(AppActions.modalRequest(payload));
    };

    return (
        <ErrorBoundary className="company-profile-about">
            <div className="d-flex company-about">
                <div className="d-flex-3 me-2 card">
                    <p className="dsl-b24 bold mt-3 pb-3 mt-lg-0">About</p>
                    {hasHtml(description) ? <div dangerouslySetInnerHTML={{ __html: description }} /> : <div className="dsl-b16 mb-0">{description}</div>}
                </div>
                <div className="d-flex-2 card">
                    <p className="dsl-b24 bold mt-3 mt-lg-0">Contact</p>
                    <div className="d-h-start py-3">
                        <p className="contact-label body-5 mb-0">Phone</p>
                        <p className="body-1 mb-0">{profile?.primary_phone || profile?.cell_phone || 'Not Provided'}</p>
                    </div>
                    <div className="d-h-start py-3">
                        <p className="contact-label body-5 mb-0">Web</p>
                        {profile?.url ? (
                            <a className="body-1 mb-0 cursor-pointer" href={websiteUrl} target="_blank">
                                {profile?.url}
                            </a>
                        ) : (
                            <p className="body-1 mb-0">Not Provided</p>
                        )}
                    </div>
                    <div className="d-h-start ai-start py-3">
                        <p className="contact-label body-5 mb-0">Address</p>
                        <p className="body-1 mb-0">{address || 'Not Provided'}</p>
                    </div>
                </div>
            </div>
            {products && (
                <div className="card mt-2">
                    <p className="dsl-b22 bold mt-3 mt-lg-0">{getCompanyName(business)} Products</p>
                    <Row className="mx-0">
                        {products.map((item, index) => {
                            if (index > 3 && !allProducts) return null;
                            const { id, name, stats, data } = item;
                            const isSponsored = data.sponsored_level === 'gold' || data.sponsored_level === 'silver';
                            return (
                                <Col
                                    className="list d-h-start flex-column mb-md-4 mb-0"
                                    key={`products-${id}`}
                                    xs={12}
                                    sm={6}
                                    md={3}
                                    onClick={() => navigate(`/business/${item?.slug}?id=${business?.id}`)}
                                >
                                    <div className="truncate dsl-b14 mt-4 bold title-sm">{name}</div>
                                    <Avatar
                                        className="mb-4 product-avatar"
                                        type="logo"
                                        url={convertUrl(isSponsored ? avatar : '', '/images/default_company.svg')}
                                        name={name}
                                        size="medium"
                                        borderWidth={1}
                                        isSponsored={isSponsored}
                                    />
                                    <div className="cursor-pointer" onClick={handleOpenRating(item)}>
                                        <Rating readonly={false} score={Math.round(Number(stats?.rating_avg) * 100) / 100 || 0} />
                                        <p className="dsl-b13">{Math.round(stats?.rating_recommended_avg * 100) / 100}% Recommended</p>
                                    </div>
                                </Col>
                            );
                        })}
                    </Row>
                    {products.length > 4 && (
                        <div className="d-h-end">
                            <Button type="medium" name={allProducts ? 'LESS' : 'SEE MORE'} className="my-0 my-md-3" onClick={handleClickAll('product')} />
                        </div>
                    )}
                </div>
            )}
            {business?.vrs?.university && (
                <div className="card mt-2">
                    <p className="dsl-b22 bold">{getCompanyName(business)} University</p>
                    <Row>
                        {business?.vrs?.university?.map((item, index) => {
                            if (index > 3 && !allUniversities) return null;
                            return (
                                <Col key={`university-${index}`} xs={3}>
                                    <video className="video-container video-container-overlay">
                                        <source type="video/mp4" src={item.vidoe} />
                                    </video>
                                    <p className="dsl-b12">{item.title}</p>
                                </Col>
                            );
                        })}
                    </Row>
                    <div className="d-h-end">
                        <Button type="medium" name={allUniversities ? 'LESS' : 'SEE MORE'} className="my-3" onClick={handleClickAll('univeristy')} />
                    </div>
                </div>
            )}
            {business?.vrs?.blogs && (
                <div className="card mt-2">
                    <p className="dsl-b22 bold">{getCompanyName(business)} Blog</p>
                    {business?.vrs?.blogs?.map((item, index) => {
                        if (index > 2 && !allBlogs) return null;
                        return (
                            <Row key={`blog-${index}`} className="mx-0 py-3">
                                <Col className="px-0" xs={3}>
                                    <Thumbnail source={item.logo} size="responsive" />
                                </Col>
                                <Col xs={9} className="pl-2">
                                    <p className="dsl-b16 bold mb-2">{item.name}</p>
                                    <p className="dsl-b14 mb-1 truncate-two">{item.data?.body}</p>
                                    <div className="d-h-between">
                                        <div className="d-h-start">
                                            <Avatar className="mr-2" type="logo" url={item.author_avatar} size="extraTiny" borderWidth={1} />
                                            <div>
                                                <p className="dsl-m12 mb-0">{`${item.author?.first_name} ${item.author?.last_name}`}</p>
                                                <p className="dsl-m12 mb-0">{item.author?.profile?.company}</p>
                                            </div>
                                        </div>
                                        <p className="dsl-b13">{moment(item.created_at).format('MMM DD, YY')}</p>
                                    </div>
                                </Col>
                            </Row>
                        );
                    })}
                    <div className="d-h-end">
                        <Button type="medium" name={allBlogs ? 'LESS' : 'SEE MORE'} className="my-3" onClick={handleClickAll('blog')} />
                    </div>
                </div>
            )}
        </ErrorBoundary>
    );
};

Detail.propTypes = {
    business: PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
    }),
};

Detail.defaultProps = {
    business: {
        id: 0,
        name: '',
    },
};

export default Detail;
